/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { ColumnsType } from 'antd/es/table';

import { InstallerToken, InstallerTokenFilters, PaginatedInstallerTokensResponse } from '@/client';
import MimicHighlighter from '@/components/MimicHighlighter';
import { PaginatedTable } from '@/components/PaginatedTable';
import { RelativeTime } from '@/components/RelativeTime';
import { MimicTranslationFunction, useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Text } from '@/primitives/Text';
import { UpdateParams } from '@/utils/params';

export type RevokedInstallerTokensTableProps = {
  installerTokensData: PaginatedInstallerTokensResponse;
  installerTokenFilters: InstallerTokenFilters;
  updateQueryParams: UpdateParams<InstallerToken, Partial<InstallerTokenFilters>>;
};

export function RevokedInstallerTokensTable({
  installerTokensData,
  updateQueryParams,
  installerTokenFilters,
}: RevokedInstallerTokensTableProps) {
  const { t } = useMimicTranslation('installerTokens');
  const columns = getRevokedInstallerTokenColumns(t, installerTokenFilters);
  return (
    <PaginatedTable
      data={installerTokensData}
      columns={columns}
      rowKey={(token) => token.tokenId}
      data-testid="revoked-installer-tokens-table"
      onParamsChange={updateQueryParams}
    />
  );
}

function getRevokedInstallerTokenColumns(
  t: MimicTranslationFunction<'installerTokens'>,
  installerTokenFilters: InstallerTokenFilters,
): ColumnsType<InstallerToken> {
  return [
    {
      title: <span data-testid="revokedInstallerTokens.name">{t('table.tokenName')}</span>,
      dataIndex: 'name',
      sorter: true,
      width: '200px',
      filterMode: 'menu',
      render: (name) => {
        return <MimicHighlighter searchText={installerTokenFilters?.name} text={name} />;
      },
    },
    {
      title: <span data-testid="revokedInstallerTokens.createdBy">{t('table.createdBy')}</span>,
      dataIndex: 'createdBy',
      render: (_, token: InstallerToken) => {
        return (
          <Text size="md" type="subtle" data-testid="revoked-installer-token-created-by">
            {token.createdBy?.displayName}
          </Text>
        );
      },
    },
    {
      title: <span data-testid="revokedInstallerTokens.dateCreated">{t('table.dateCreated')}</span>,
      dataIndex: 'dateCreated',
      sorter: true,
      render: (dateCreated: string) => (
        <Text size="md" type="subtle" data-testid="revoked-installer-token-date-created">
          <RelativeTime date={dateCreated} />
        </Text>
      ),
      width: '180px',
    },
    {
      title: <span data-testid="revokedInstallerTokens.timesUsed">{t('table.timesUsed')}</span>,
      dataIndex: 'quotaUsed',
      sorter: true,
      render: (quotaUsed: number) => (
        <Text size="md" type="subtle" data-testid="revoked-installer-token-times-used">
          {quotaUsed}
        </Text>
      ),
      width: '140px',
    },
    {
      title: <span data-testid="revokedInstallerTokens.revokedBy">{t('table.revokedBy')}</span>,
      dataIndex: 'revokedBy',
      render: (_, token: InstallerToken) => {
        return (
          <Text size="md" type="subtle" data-testid="revoked-installer-token-revoked-by">
            {token.revokedBy?.displayName || '-'}
          </Text>
        );
      },
    },
    {
      title: <span data-testid="revokedInstallerTokens.dateRevoked">{t('table.dateRevoked')}</span>,
      dataIndex: 'dateRevoked',
      render: (dateRevoked: string | null) => (
        <Text size="md" type="subtle" data-testid="revoked-installer-token-date-revoked">
          {dateRevoked ? <RelativeTime date={dateRevoked} /> : '-'}
        </Text>
      ),
      width: '180px',
    },
  ];
}
